/**
 * Reads this backend's runtime identity (federation node id + version) from the
 * public /info endpoint.
 *
 * The node id rarely changes across a deployment, so the result is cached at the
 * module level: the first consumer fetches, everyone else reads the shared value.
 * The underlying GET also opts into the api client's short-TTL cache, so even the
 * first fetch dedups if several components mount at once.
 *
 *   const { nodeId } = useNodeInfo();
 */

import { useState, useEffect } from 'react';
import { useApi } from './use_api';

// Shared across the app so the nav bar (and any future consumer) fetch once.
let cachedInfo = null;

export function useNodeInfo() {
    const api = useApi();
    const [info, setInfo] = useState(cachedInfo);
    // Whether the /info round trip has finished, either way. Tracked separately
    // from `info` because a failed fetch leaves info null forever, and a
    // consumer waiting on it would hang rather than fall back.
    const [settled, setSettled] = useState(cachedInfo !== null);

    useEffect(() => {
        if (cachedInfo) return;
        let alive = true;
        api.get('/info', { cache: true })
            .then(data => { cachedInfo = data; if (alive) { setInfo(data); setSettled(true); } })
            .catch(() => { if (alive) setSettled(true); /* consumers fall back to a static label */ });
        return () => { alive = false; };
    }, [api]);

    return {
        // False only while the first /info fetch is in flight. The flags below
        // are deliberately permissive until then, which is right for deciding
        // whether to show chrome, but wrong for UI whose whole job is reporting
        // flag state (the /map page) — that should wait on this instead, or it
        // will show everything as on and then flip most of it off.
        ready: settled,
        nodeId: info?.node_id || null,
        // The node's registry slug (backend tenancy.py); null on a deployment
        // that runs without a registry.
        nodeSlug: info?.node_slug || null,
        // Always set by the backend (federation id, else the k8s namespace,
        // else "Vellby"): names the tab and seeds the node's generated avatar.
        nodeName: info?.node_name || null,
        version: info?.version || null,
        federationEnabled: !!info?.federation_enabled,
        // Node feature toggles (#36). Until /info resolves we assume the feature is
        // on (permissive) so core UI does not flash hidden then reappear; a node
        // with the feature off flips it false once the cached fetch completes.
        featureMessaging: info?.feature_messaging !== false,
        // Same permissive-until-known convention as featureMessaging above.
        featureHangouts: info?.feature_hangouts !== false,
        // Sub-flag of featureHangouts — the backend's /info already folds
        // feature_hangouts into this value, so a single check here is enough
        // (no need to AND it with featureHangouts again). Gates the Schedule
        // button/list on entities and the /calendar page + nav entry.
        featureHangoutSchedule: info?.feature_hangout_schedule !== false,
        // Opposite convention on purpose, same as featureRoadmapHints: this one
        // defaults OFF on the backend (config.py feature_hangout_facilitator,
        // roadmap Phase 6's capstone), so "unknown" must read as false too —
        // otherwise the breakout button would flash on before /info resolves on
        // nodes that keep it disabled.
        featureHangoutFacilitator: info?.feature_hangout_facilitator === true,
        // Defaults OFF on the backend (config.py feature_gathering), so
        // "unknown" reads as false too — the nav entry shouldn't flash on
        // before /info resolves on nodes without it.
        featureGathering: info?.feature_gathering === true,
        // Sub-flag of featureGathering (the backend folds feature_gathering
        // into it): joining the gathering as a guest, without an account, at
        // /gathering/join. Default OFF until known ON, same as featureGathering.
        featureGatheringGuestAccess: info?.feature_gathering_guest_access === true,
        // Same permissive-until-known convention as featureMessaging above.
        // Also gates a fetch: use_notifications polls /api/notifications every
        // 15s, and those routes are unmounted on a node with this off.
        featureNotifications: info?.feature_notifications !== false,
        // Same permissive-until-known convention as featureMessaging above.
        // NOTE: unlike the others this also gates a *fetch* — /api/billing/status
        // is unmounted on a node with the feature off, so use_subscription waits
        // for this to resolve rather than firing a request that would 404.
        featureSubscription: info?.feature_subscription !== false,
        // Per-user credits (config.py feature_credits). Defaults OFF on the
        // backend, so "unknown" reads as false — the Credits card and the
        // balance tag on the call waiting rooms must not flash in before /info
        // resolves. Like featureSubscription this also gates a fetch:
        // /api/credits is unmounted on a node with it off.
        featureCredits: info?.feature_credits === true,
        // Sub-flag (the backend folds feature_credits into it): whether a
        // balance of zero or less currently blocks joining a room.
        creditsEnforce: info?.credits_enforce === true,
        // What one participant-minute in a room costs; 1 until known.
        creditsPerParticipantMinute: Number.isFinite(info?.credits_per_participant_minute)
            ? info.credits_per_participant_minute : 1,
        // Opposite convention on purpose: this one defaults OFF on the backend
        // (config.py feature_roadmap_hints), so "unknown" must read as false too —
        // otherwise the roadmap link/ribbon would flash on before /info resolves
        // on nodes that keep it disabled.
        featureRoadmapHints: info?.feature_roadmap_hints === true,
        // Same permissive-until-known convention as featureMessaging above.
        featureEntityTypeIdentifiers: info?.feature_entity_type_identifiers !== false,
        // Defaults OFF on the backend (config.py feature_multiple_descriptions),
        // so "unknown" reads as false — no extra-description rows flashing in
        // before /info resolves.
        featureMultipleDescriptions: info?.feature_multiple_descriptions === true,
        // Same permissive-until-known convention as featureMessaging: defaults
        // ON in config.py, so "unknown" reads as on and the Listed switch does
        // not flash away on nodes that keep it. Off means the node has no
        // listed/unlisted concept at all — the backend stops filtering on it,
        // so hiding the controls is the whole of the client's job.
        featureEntityListing: info?.feature_entity_listing !== false,
        // Defaults ON on the backend, so permissive-until-known like the others.
        featureTrustPage: info?.feature_trust_page !== false,
        // Defaults ON on the backend (config.py feature_registration_approval),
        // so permissive-until-known: a superadmin on a node that keeps approval
        // must not see the Unapproved tab flash away. Off means the node has no
        // approval step — the backend lets pending signups in by itself — so
        // hiding that tab is the whole of the client's job.
        featureRegistrationApproval: info?.feature_registration_approval !== false,
        // Defaults OFF on the backend (config.py feature_branding), so "unknown"
        // reads as false — the Branding tab must not flash in before /info
        // resolves on nodes that keep it disabled.
        featureBranding: info?.feature_branding === true,
        // Defaults OFF on the backend (config.py feature_real_pictures): the
        // upload/remove controls on an entity's picture must not flash in
        // before /info resolves on nodes that keep it disabled.
        featureRealPictures: info?.feature_real_pictures === true,
        // Self-service node creation (config.py feature_self_service_nodes):
        // defaults OFF on the backend, so "unknown" reads as false — the New
        // node entry must not flash in before /info resolves. Also gates fetches:
        // /api/node-signups is unmounted on a node with it off.
        featureSelfServiceNodes: info?.feature_self_service_nodes === true,
        // Whether this is the deployment's platform node, where nodes are
        // created from (routers/node_signups.py). False until known.
        nodeIsPlatform: info?.node_is_platform === true,
        // null until known; consumers should treat null as "all types allowed".
        enabledEntityTypes: info?.enabled_entity_types || null,
        // Connecting entities to each other (and searching for connections) only
        // makes sense when a context or relationship "hub" type is enabled — every
        // connectable pair includes one (see backend assert_connectable_types).
        // Derived, not a separate flag: null (not yet known) is permissive, same
        // convention as enabledEntityTypes above.
        connectionsEnabled: info?.enabled_entity_types
            ? info.enabled_entity_types.includes('context') || info.enabled_entity_types.includes('relationship')
            : true,
    };
}

export default useNodeInfo;
